/**
 * Сотрудники фирмы: приглашения и список записей членства.
 *
 * Приглашение адресовано **каналу**, а не аккаунту: профиля приглашённому оно
 * не создаёт, и вторую половину выполняет сам приглашённый своим токеном.
 * Канал в теле ровно один — какой именно, выбирает человек, а проверку «ровно
 * один канал указан» и годность значения выполняет сервер. Проверок формата
 * здесь нет ни одной.
 *
 * Список членства показывает **каждую** запись, как она пришла: закрытые
 * остаются, у одного человека записей может быть несколько. Скрыть или свести
 * их значило бы показать не то, что отдал сервер, — из чего складывается
 * действующее участие, решает он.
 *
 * Кнопки смены роли и вывода сотрудника здесь нет: операций для них в
 * контракте волны нет вовсе, а заготовка под недостижимое состояние — заглушка.
 *
 * Роль и статус показаны подписью по ключу строителя: перечни значений
 * порождаются из контракта, и второго их экземпляра в треке нет.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type { InvitationInput } from '../api/client';
import type { CompanyMember, Invitation, MembershipRole } from '../api/contract';
import { MEMBERSHIP_ROLES } from '../api/generated/enums';
import { invitationStatusStringKey, memberStatusStringKey, roleStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { memberDocumentsPath, memberRightsPath } from '../router';
import {
  createFieldMessageSlot,
  enumLabel,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

/** Канал приглашения. Ровно один из двух — выбирает человек. */
export type InviteChannel = 'phone' | 'email';

/** Роль приглашения — перечень контракта, а не свободная строка. */
export type InviteRole = MembershipRole;

export interface InviteInput {
  readonly channel: InviteChannel;
  readonly value: string;
  readonly role: InviteRole;
}

export interface MembersScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

export interface MembersScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  readonly members: readonly CompanyMember[];
  readonly invitations: readonly Invitation[];
  load(): Promise<void>;
  invite(input: InviteInput): Promise<void>;
  revokeInvitation(invitationId: string): Promise<void>;
  resendInvitation(invitationId: string): Promise<void>;
}

const DEFAULT_CHANNEL: InviteChannel = 'phone';

export function createMembersScreen(options: MembersScreenOptions): MembersScreen {
  const { client, strings, companyId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const form = document.createElement('form');
  const channels = document.createElement('div');
  const phoneChoice = document.createElement('button');
  const emailChoice = document.createElement('button');
  const valueCaption = document.createElement('label');
  const valueCaptionText = document.createElement('span');
  const valueField = document.createElement('input');
  const valueMessage = createFieldMessageSlot();
  const roleCaption = document.createElement('label');
  const roleCaptionText = document.createElement('span');
  const roleField = document.createElement('select');
  const roleMessage = createFieldMessageSlot();
  const submit = document.createElement('button');
  const membersList = document.createElement('ul');
  const invitationsHeading = document.createElement('h2');
  const invitationsList = document.createElement('ul');
  /**
   * Место сообщения сервера, которому в форме места нет.
   *
   * Отказ на действие строки приглашения сервер называет полем своего тела —
   * `invitation`, — и такого поля в форме приглашения нет и быть не может:
   * форма про адрес и роль, а отказ про уже существующую запись. Сообщению
   * нужно место вне формы, иначе оно исчезает целиком, и человек видит
   * нажатие, на которое ничего не ответило.
   *
   * Класс тот же, что у мест внутри формы: это то же самое — место, куда
   * ложится текст сервера. Второй класс завёл бы вторую форму одного и того
   * же и второй набор правил показа.
   */
  const notice = createFieldMessageSlot();

  element.className = 'screen screen-members';
  form.className = 'form';
  channels.className = 'channels';
  valueCaption.className = 'form-field';
  valueCaptionText.className = 'form-field__caption';
  roleCaption.className = 'form-field';
  roleCaptionText.className = 'form-field__caption';
  submit.className = 'action';

  heading.textContent = strings.members_title;
  valueCaptionText.textContent = strings.members_invite;
  roleCaptionText.textContent = strings.invite_role_label;
  invitationsHeading.textContent = strings.invitations_title;
  phoneChoice.type = 'button';
  phoneChoice.textContent = strings.invite_channel_phone;
  emailChoice.type = 'button';
  emailChoice.textContent = strings.invite_channel_email;
  submit.type = 'submit';
  submit.textContent = strings.invite_submit;
  roleField.name = 'role';

  // Выбор роли строится **порождённым** перечнем контракта: рукописный список
  // ролей разошёлся бы с документом молча. Подпись — по ключу строителя.
  for (const role of MEMBERSHIP_ROLES) {
    const choice = document.createElement('option');

    choice.value = role;
    choice.textContent = enumLabel(strings, roleStringKey(role));
    roleField.append(choice);
  }

  channels.append(phoneChoice, emailChoice);
  valueCaption.append(valueCaptionText, valueField, valueMessage);
  roleCaption.append(roleCaptionText, roleField, roleMessage);
  form.append(channels, valueCaption, roleCaption, submit);

  let state: ScreenState | null = 'loading';
  let members: readonly CompanyMember[] = [];
  let invitations: readonly Invitation[] = [];
  let channel: InviteChannel = DEFAULT_CHANNEL;

  function memberRow(member: CompanyMember): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');
    const rights = document.createElement('a');
    const papers = document.createElement('a');

    node.className = 'member-row';
    line.textContent = [
      member.phone ?? member.email ?? '',
      enumLabel(strings, roleStringKey(member.role)),
      enumLabel(strings, memberStatusStringKey(member.status)),
    ].join(' ');

    // Права и документы принадлежат **записи** членства, и попасть в них можно
    // отсюда: маршрут, до которого нет ни одной ссылки, существует только для
    // того, кто наберёт адрес руками. Адреса собирают единственные строители.
    rights.className = 'company-link';
    rights.href = memberRightsPath(companyId, member.id);
    rights.textContent = strings.rights_title;

    papers.className = 'company-link';
    papers.href = memberDocumentsPath(companyId, member.id);
    papers.textContent = strings.member_documents_title;

    node.append(line, rights, papers);

    return node;
  }

  function invitationRow(invitation: Invitation): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');
    const revoke = document.createElement('button');
    const resend = document.createElement('button');

    node.className = 'invitation-row';
    // Статус приглашения — подписью по ключу строителя, как роль рядом с ним
    // (долг `TD-68`, решение `D188`). Прежде здесь стояло **значение, как оно
    // пришло**: человек читал `pending` и `revoked` английскими словами на
    // любом языке, тогда как соседние величины той же строки были подписаны.
    // Дыра была в словаре имён — четыре префикса объявлены, приглашения
    // пропущены, — и запрет литералов её не видел: значение приезжало ответом
    // сервера, а не писалось рукой.
    line.textContent = [
      invitation.value,
      enumLabel(strings, roleStringKey(invitation.role)),
      enumLabel(strings, invitationStatusStringKey(invitation.status)),
    ].join(' ');

    revoke.type = 'button';
    revoke.className = 'action';
    revoke.textContent = strings.invitation_revoke;
    revoke.addEventListener('click', () => {
      void revokeInvitation(invitation.id);
    });

    resend.type = 'button';
    resend.className = 'action';
    resend.textContent = strings.invitation_resend;
    resend.addEventListener('click', () => {
      void resendInvitation(invitation.id);
    });

    node.append(line, revoke, resend);

    return node;
  }

  function render(): void {
    // Сообщение, пережившее перерисовку, отвечает на вопрос, которого уже
    // никто не задавал: снимается оно тем же одним движением, что и места
    // сообщений внутри формы.
    notice.textContent = '';

    if (state !== null) {
      element.replaceChildren(renderScreenState(state, strings));

      return;
    }

    // Имя поля — то же, что имя поля тела: по нему сервер называет место, куда
    // положить своё сообщение. Оно стоит в разметке, а не в разборе.
    valueField.name = channel;
    valueField.id = channel;
    phoneChoice.setAttribute('aria-current', String(channel === 'phone'));
    emailChoice.setAttribute('aria-current', String(channel === 'email'));

    const memberRows: HTMLElement[] = [];

    for (const member of members) {
      memberRows.push(memberRow(member));
    }

    const invitationRows: HTMLElement[] = [];

    for (const invitation of invitations) {
      invitationRows.push(invitationRow(invitation));
    }

    membersList.replaceChildren(...memberRows);
    invitationsList.replaceChildren(...invitationRows);
    element.replaceChildren(
      heading,
      notice,
      form,
      membersList,
      invitationsHeading,
      invitationsList,
    );
  }

  /**
   * Исход неудачного обращения — одним местом.
   *
   * Сервер, назвавший поля, получает свои сообщения у этих полей: человеку
   * есть что исправлять, и содержимое экрана остаётся. Не назвавший ни одного
   * оставляет экрану только состояние отказа.
   *
   * Названное поле не обязано быть полем формы, и это не редкость: отказ на
   * переотправку отозванного приглашения приходит с полем `invitation`.
   * Сообщения, которым места у полей не нашлось, показываются общим текстом —
   * тем же дословным текстом сервера, но не у поля. Уронить их значило бы
   * поглотить отказ сервера: сервер ответил, человек не увидел ничего.
   */
  function failureOf(error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;

    if (failure !== null && failure.details.length > 0) {
      render();

      const homeless = renderFieldMessages(form, failure.details);
      const texts: string[] = [];

      for (const detail of homeless) {
        texts.push(detail.message);
      }

      notice.textContent = texts.join(' ');

      return;
    }

    state = screenStateFor({ kind: 'failed', code: failure === null ? null : failure.code });
    render();
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    members = [];
    invitations = [];
    render();

    try {
      members = await client.listCompanyMembers(companyId);
      invitations = await client.listCompanyInvitations(companyId);
      state = null;
    } catch (error) {
      members = [];
      invitations = [];
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function invite(input: InviteInput): Promise<void> {
    renderFieldMessages(form, []);

    // Ключ канала ровно один, и второго в теле нет вовсе: пара необязательных
    // полей позволила бы отправить оба ключа сразу, и поймал бы это сервер.
    const body: InvitationInput =
      input.channel === 'phone'
        ? { phone: input.value, role: input.role }
        : { email: input.value, role: input.role };

    try {
      const created = await client.createInvitation(companyId, body);

      // Перерисовка по ответу: показывается созданное сервером приглашение, а
      // не собранное клиентом из введённого.
      invitations = [...invitations, created];
      state = null;
      render();
    } catch (error) {
      failureOf(error);
    }
  }

  async function reloadInvitations(): Promise<void> {
    try {
      invitations = await client.listCompanyInvitations(companyId);
      render();
    } catch (error) {
      failureOf(error);
    }
  }

  async function revokeInvitation(invitationId: string): Promise<void> {
    try {
      await client.revokeInvitation(companyId, invitationId);
    } catch (error) {
      failureOf(error);

      return;
    }

    await reloadInvitations();
  }

  async function resendInvitation(invitationId: string): Promise<void> {
    // Переотправка — своя операция, и второго приглашения она не создаёт: тем
    // ли оно осталось, решает сервер.
    try {
      await client.resendInvitation(companyId, invitationId);
    } catch (error) {
      failureOf(error);

      return;
    }

    await reloadInvitations();
  }

  function selectChannel(chosen: InviteChannel): void {
    channel = chosen;
    valueField.value = '';
    render();
  }

  phoneChoice.addEventListener('click', () => {
    selectChannel('phone');
  });

  emailChoice.addEventListener('click', () => {
    selectChannel('email');
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    // Выбор построен порождённым перечнем контракта, и другого значения в нём
    // нет: приведение здесь называет то, что уже верно по построению.
    void invite({ channel, value: valueField.value, role: roleField.value as InviteRole });
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get members(): readonly CompanyMember[] {
      return members;
    },

    get invitations(): readonly Invitation[] {
      return invitations;
    },

    load,
    invite,
    revokeInvitation,
    resendInvitation,
  };
}
